import React, { useLayoutEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { ActivityBrief } from '../concierge/ActivityBrief';
import { RoutePlanner } from '../concierge/RoutePlanner';
import { BrandSignature } from '../ui/BrandSignature';
import { CinematicButton } from '../ui/CinematicButton';
import { JourneyChapter as JourneyChapterContent, LocalizedContent, whatsappLink } from '../../data/siteContent';
type Props = {
  chapter: JourneyChapterContent;
  content: LocalizedContent;
  isOpening?: boolean;
  onAdvance: () => void;
};

// One immersive chapter, carrying both the cinematic beat and the complete source content.
export function JourneyChapter({
  chapter,
  content,
  isOpening = false,
  onAdvance
}: Props) {
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const alignment = chapter.align === 'center' ? 'items-center text-center' : 'items-start text-left';
  const action = isOpening ? <CinematicButton onClick={onAdvance} variant="outline" data-id="element-119" data-mp="2ibc2d37i0fg2">{content.journey.startLabel}</CinematicButton> : chapter.ctaLabel && chapter.ctaMessage ? <CinematicButton href={whatsappLink(chapter.ctaMessage)} variant="outline" data-id="element-120" data-mp="2ebuz8gfjnue2">
      {chapter.ctaLabel}
    </CinematicButton> : null;
  useLayoutEffect(() => {
    const scrollContent = scrollContentRef.current;
    if (!scrollContent) return;
    const updateScrollHint = () => {
      const isMobile = window.matchMedia('(max-width: 639px)').matches;
      const remainingContent = scrollContent.scrollHeight - scrollContent.clientHeight - scrollContent.scrollTop;
      setShowScrollHint(isMobile && remainingContent > 8);
    };
    updateScrollHint();
    scrollContent.addEventListener('scroll', updateScrollHint, {
      passive: true
    });
    window.addEventListener('resize', updateScrollHint);
    const resizeObserver = new ResizeObserver(updateScrollHint);
    resizeObserver.observe(scrollContent);
    return () => {
      scrollContent.removeEventListener('scroll', updateScrollHint);
      window.removeEventListener('resize', updateScrollHint);
      resizeObserver.disconnect();
    };
  }, [chapter.id]);
  return <div className={`scene-copy absolute inset-x-0 top-[5.5rem] bottom-[calc(5.25rem+env(safe-area-inset-bottom))] z-10 flex min-h-0 px-5 sm:top-auto sm:bottom-[9%] sm:px-10 lg:px-[9vw] 2xl:px-[11vw] ${alignment}`} data-id="element-121" data-mp="3gqc9p3fy8jm9">
      <div className="journey-chapter-scroll-frame relative h-full max-h-full w-full max-w-3xl sm:h-auto 2xl:max-w-4xl" data-id="element-122" data-mp="3uok1kwy4ouqk">
        <div ref={scrollContentRef} className="journey-chapter-content h-full max-h-full overflow-y-auto overscroll-contain pr-1 touch-pan-y sm:h-auto sm:max-h-none sm:overflow-visible sm:pr-0" data-id="element-123" data-mp="3h7j8elieb92">
          <BrandSignature content={content} compact mobileHidden align={chapter.align === 'center' ? 'center' : 'left'} className="mb-4 lg:mb-7 2xl:mb-8" data-id="element-124" data-mp="d826nmahrgw3" />
          <p className="mb-3 text-[9px] font-medium uppercase tracking-[0.26em] text-[var(--ms-gold-soft)] sm:mb-4 sm:text-[10px] sm:tracking-[0.32em] lg:text-xs 2xl:mb-5 2xl:text-sm" data-id="element-125" data-mp="h7jdok5kgm3d">
          {chapter.eyebrow}
        </p>
        <h1 className="font-editorial text-[clamp(2.15rem,10vw,3.1rem)] font-medium leading-[0.96] tracking-[-0.045em] text-white sm:text-[clamp(2.75rem,6vw,6.25rem)] sm:leading-[0.94] sm:tracking-[-0.05em]" data-id="element-126" data-mp="21rogzouie083">
          {chapter.title}
        </h1>
        <p className="mt-3 max-w-2xl text-[13px] leading-relaxed text-white/82 sm:mt-5 sm:text-base" data-id="element-127" data-mp="1ssb6qtyk97mb">{chapter.lead}</p>

        <ActivityBrief chapterId={chapter.id} content={content} data-id="element-128" data-mp="2pj3nlttbj3p5" />
        {chapter.id === 'routes' && <RoutePlanner content={content} data-id="element-129" data-mp="2lg7a2himxxs2" />}

        {(chapter.highlights?.length || chapter.details.length) && <details className={`chapter-notes mt-4 max-w-2xl sm:mt-5 ${chapter.align === 'center' ? 'text-left' : ''}`} data-id="element-130" data-mp="2gmcmh2zwsynp">
            <summary className="cursor-pointer list-none text-[10px] font-medium uppercase tracking-[0.18em] text-[var(--ms-gold-soft)] outline-none focus-visible:text-white sm:text-[11px] sm:tracking-[0.2em]" data-id="element-131" data-mp="394qc4fb3debp">
              <span className="chapter-notes__closed" data-id="element-132" data-mp="2nyhj6g5n1u7x">{content.journey.detailsLabel}</span>
              <span className="chapter-notes__open" data-id="element-133" data-mp="2sydc9kcss18p">{content.journey.hideDetailsLabel}</span>
            </summary>
            <div className="mt-4 border-l border-[var(--ms-gold-soft)]/55 pl-4 sm:pl-5" data-id="element-134" data-mp="2eyui5vgtmqfe">
              {chapter.highlights && <ul className="mb-4 flex flex-wrap gap-2" aria-label={content.journey.highlightsLabel} data-id="element-135" data-mp="2jxp5napqh35u">
                  {chapter.highlights.map(highlight => <li key={highlight} className="border border-white/20 bg-black/20 px-2.5 py-1 text-[11px] leading-tight text-white/85 backdrop-blur-sm" data-id="element-136" data-mp="3bolapp5jy3ik">
                      {highlight}
                    </li>)}
                </ul>}
              <div className="space-y-3" data-id="element-137" data-mp="3qlm0hn46noee">
                {chapter.details.map(detail => <p key={detail} className="text-sm leading-relaxed text-white/78" data-id="element-138" data-mp="3vcc9dabti9du">{detail}</p>)}
              </div>
            </div>
          </details>}

          {action && <div className="mt-5 pb-1 sm:mt-6 sm:pb-0" data-id="element-139" data-mp="8jdu9bc7uebi">{action}</div>}
        </div>
        <div className={`journey-scroll-cue ${showScrollHint ? 'journey-scroll-cue--visible' : ''}`} aria-hidden="true" data-id="element-140" data-mp="4i2ahmg0wnb8">
          <span data-id="element-141" data-mp="dcgn1h965jqn">{content.journey.scrollHint}</span>
          <ChevronDownIcon className="journey-scroll-cue__icon" data-id="element-142" data-mp="ddrovbj20hf4" />
        </div>
      </div>
    </div>;
}